import React, { useState, useEffect, useRef } from "react";
import localforage from "localforage";
import api from "../utils/axiosConfig";
import SideBar from "./SideBar";
import AWS from "aws-sdk";
import { useNavigate } from "react-router-dom";

AWS.config.update({
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
});

const s3 = new AWS.S3();
const API_URL = process.env.REACT_APP_API_URL;

const LeaveRequest = () => {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const allowedDocumentTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    userId: "",
    receiverEmail: "",
    receiverName: "",
    reportingManagerEmail: "",
    reportingManager: "",
    reportingManagerEmail2: "",
    reportingManager2: "",
    reportingManagerEmail1: "",
    reportingManager1: "",
    reasonForLeave: "",
    startDate: "",
    endDate: "",
    summaryForLeave: "",
    leaveType: "",
    document: null,
    location: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // success message string (you were using boolean earlier, but setting string)
  const [success, setSuccess] = useState("");
  const [emailInfo, setEmailInfo] = useState(null); // { status, error }

  const [documentError, setDocumentError] = useState("");
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const name = await localforage.getItem("userName");
        const email = await localforage.getItem("email");
        const userId = await localforage.getItem("ID");
        const location = await localforage.getItem("location");

        setFormData((prev) => ({
          ...prev,
          email: email || "",
          location: location || "",
          name: name || "",
          userId: userId || "",
        }));

        if (email) {
          const response = await api.get(
            `${API_URL}/employeedata/get-manager-email/${email}`,
          );

          setFormData((prev) => ({
            ...prev,
            receiverEmail: response.data.reportingManagerEmail || "",
            receiverName: response.data.reportingManager || "",
            reportingManagerEmail: response.data.reportingManagerEmail || "",
            reportingManager: response.data.reportingManager || "",
            reportingManagerEmail2: response.data.reportingManagerEmail2 || "",
            reportingManager2: response.data.reportingManager2 || "",
            reportingManagerEmail1: response.data.reportingManagerEmail1 || "",
            reportingManager1: response.data.reportingManager1 || "",
          }));
        }
      } catch (err) {
        console.error("Error fetching user or manager email", err);
        setError("Error fetching manager email. Please try again later.");
      }
    };

    fetchUserData();
  }, []);

  const validateDates = () => {
    const { startDate, endDate } = formData;

    if (!startDate || !endDate) return true;

    if (new Date(endDate) < new Date(startDate)) {
      setDateError("End date cannot be earlier than start date.");
      return false;
    }
    setDateError("");
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFormData((prev) => ({ ...prev, document: null }));
      setDocumentError("");
      return;
    }

    if (allowedDocumentTypes.includes(file.type)) {
      setFormData((prev) => ({ ...prev, document: file }));
      setDocumentError("");
    } else {
      setFormData((prev) => ({ ...prev, document: null }));
      setDocumentError(
        "Invalid document type. Please upload a PDF, DOC, or DOCX file.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const uploadFileToS3 = async (file, folder) => {
    const bucket = process.env.REACT_APP_AWS_S3_BUCKET;

    if (!bucket) {
      throw new Error("Missing REACT_APP_AWS_S3_BUCKET env var.");
    }

    const params = {
      Bucket: bucket,
      Key: `${folder}/${Date.now()}_${file.name}`,
      Body: file,
      ContentType: file.type,
    };

    const uploadResult = await s3.upload(params).promise();
    return uploadResult.Location;
  };

  const resetFormAfterSubmit = () => {
    setFormData((prev) => ({
      ...prev,
      reasonForLeave: "",
      startDate: "",
      endDate: "",
      summaryForLeave: "",
      leaveType: "",
      document: null,
    }));

    setDocumentError("");
    setDateError("");

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setSuccess("");
    setEmailInfo(null);

    if (!validateDates()) return;

    setLoading(true);

    try {
      const documentUrl = formData.document
        ? await uploadFileToS3(formData.document, "documents")
        : null;

      const updatedFormData = {
        ...formData,
        document: documentUrl,
      };

      const token = await localforage.getItem("token");

      const response = await api.post(
        `${API_URL}/leavedata/leave`,
        updatedFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // ✅ NEW backend response shape:
      // { leave: savedLeave, email: { status, error } }
      const leave = response.data?.leave;
      const email = response.data?.email;

      const leaveCode = leave?.leaveCode || "N/A";
      setEmailInfo(email || null);

      if (email?.status === "sent") {
        setSuccess(
          `Leave request submitted successfully! Leave Code: ${leaveCode}. Mail has been sent to the reporting manager and HR.`,
        );
      } else if (email?.status === "failed") {
        setSuccess(
          `Leave request submitted successfully! Leave Code: ${leaveCode}. Email could not be sent right now (SES not verified / temporary issue). Your request is saved and will be processed.`,
        );
      } else {
        setSuccess(
          `Leave request submitted successfully! Leave Code: ${leaveCode}. Email notification is currently disabled. Your request is saved and will be processed.`,
        );
      }

      resetFormAfterSubmit();

      setTimeout(() => {
        setSuccess("");
        navigate("/leave-data");
      }, 3000);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err.response?.data?.error || "Something went wrong");
      setTimeout(() => setError(null), 6000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        <div className="form-container">
          <div className="form-header">
            <h2 className="form-title">Apply for Leave</h2>
            <p className="form-subtitle">Submit your leave request</p>
          </div>

          {success && (
            <div className="alert alert-success">
              {success}
              {emailInfo?.status === "failed" && (
                <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
                  Email status: <b>Failed</b>
                  {emailInfo?.error ? (
                    <span style={{ display: "block", marginTop: 4 }}>
                      <small>{emailInfo.error}</small>
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}
          {dateError && <div className="alert alert-error">{dateError}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3 className="form-section-title">Manager Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Mail to</label>
                  <input
                    type="email"
                    name="receiverEmail"
                    value={formData.receiverEmail}
                    onChange={handleChange}
                    required
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label>Reporting Manager</label>
                  <input
                    type="text"
                    name="receiverName"
                    value={formData.receiverName}
                    onChange={handleChange}
                    required
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="form-section-title">Leave Details</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Request leave from</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Till</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Select leave type</label>
                  <select
                    name="leaveType"
                    value={formData.leaveType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="sickLeave">Sick Leave</option>
                    <option value="regularizationLeave">
                      Leave Regularization
                    </option>
                    <option value="compensationLeave">
                      Compensation Leave
                    </option>
                    <option value="onOfficeDuty">On Office Duty</option>
                    <option value="paidLeave">Paid Leave</option>
                    <option value="halfDayLeave">Half Day Leave</option>
                    <option value="menstrualLeave">Menstrual Leave</option>
                    <option value="restrictedHoliday">Restricted Holiday</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Attach Document (Optional)</label>
                  <input
                    type="file"
                    name="document"
                    ref={fileInputRef}
                    onChange={handleDocumentChange}
                  />
                  {documentError && (
                    <span className="error">{documentError}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  name="reasonForLeave"
                  value={formData.reasonForLeave}
                  onChange={handleChange}
                  placeholder="Enter the subject..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Reason for leave</label>
                <textarea
                  name="summaryForLeave"
                  value={formData.summaryForLeave}
                  onChange={handleChange}
                  placeholder="Enter the reason for leave..."
                  rows="4"
                  required
                ></textarea>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/leave-section")}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LeaveRequest;
